// react
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// api
import { useAlarmTypesGetCollectionAsyncByqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/alarms';
import {
  useDeleteAlarmGroupMutation,
  useGetAlarmGroupsByAccountQuery,
  usePostAlarmGroupMutation,
} from '@/api/recalc/endpoints/alarmGroupsApiSlice';

// components
import { useSessionAuth } from '@/contexts/SessionAuthContext';
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import {
  findAutoCreatedEnergyXplorerAlarmGroup,
  getEnergyXplorerAlarmTypeIds,
  getMissingEnergyXplorerAlarmTypeIds,
} from '../utils/energyXplorerAlarmTypes';

type UseEnergyXplorerAlarmSetupProps = {
  accountId: number | undefined;
  enabled?: boolean;
};

export function useEnergyXplorerAlarmSetup({
  accountId,
  enabled = true,
}: UseEnergyXplorerAlarmSetupProps) {
  const { t } = useTranslation('energy-analysis');
  const dispatch = useDispatch();
  const { session } = useSessionAuth();

  const userId = Number(session?.claims?.sub);
  const hasValidUserId = Number.isFinite(userId);
  const shouldFetch = enabled && typeof accountId === 'number';
  const automaticGroupName = t('alarm_setup.group_name', {
    defaultValue: 'EnergyXplorer alarms',
  });

  const {
    currentData: alarmGroups,
    isLoading: isAlarmGroupsLoading,
    isFetching: isAlarmGroupsFetching,
  } = useGetAlarmGroupsByAccountQuery(accountId, {
    skip: !shouldFetch,
  });

  const {
    currentData: alarmTypes,
    isLoading: isAlarmTypesLoading,
    isFetching: isAlarmTypesFetching,
  } = useAlarmTypesGetCollectionAsyncByqueryOptionsQuery(
    {
      orderby: 'AlarmTypeId',
      top: 10000,
    },
    {
      skip: !shouldFetch,
    },
  );

  const [postAlarmGroup, { isLoading: isEnabling }] =
    usePostAlarmGroupMutation();
  const [deleteAlarmGroup, { isLoading: isDeletingAutoCreatedAlarmGroup }] =
    useDeleteAlarmGroupMutation();

  const resolvedAlarmGroups = alarmGroups ?? [];
  const resolvedAlarmTypes = alarmTypes ?? [];
  const isSetupStateLoading =
    shouldFetch &&
    ((alarmGroups === undefined &&
      (isAlarmGroupsLoading || isAlarmGroupsFetching)) ||
      (alarmTypes === undefined &&
        (isAlarmTypesLoading || isAlarmTypesFetching)));

  const energyXplorerAlarmTypeIds =
    getEnergyXplorerAlarmTypeIds(resolvedAlarmTypes);
  const autoCreatedAlarmGroup = hasValidUserId
    ? findAutoCreatedEnergyXplorerAlarmGroup(
        resolvedAlarmGroups,
        userId,
        automaticGroupName,
      )
    : null;
  const missingAlarmTypeIds = hasValidUserId
    ? getMissingEnergyXplorerAlarmTypeIds(
        resolvedAlarmGroups,
        userId,
        energyXplorerAlarmTypeIds,
      )
    : [];

  const shouldShowSetupPrompt =
    !isSetupStateLoading &&
    shouldFetch &&
    hasValidUserId &&
    energyXplorerAlarmTypeIds.length > 0 &&
    missingAlarmTypeIds.length > 0;

  const enableAlarms = async () => {
    if (!accountId || !hasValidUserId || missingAlarmTypeIds.length === 0) {
      return;
    }

    try {
      await postAlarmGroup({
        Name: t('alarm_setup.group_name', {
          defaultValue: 'EnergyXplorer alarms',
        }),
        Account: { AccountId: accountId },
        AlarmSubscriptions: [
          {
            User: { UserId: userId },
            FeedSeverity: 'Information',
            EmailSeverity: 'Information',
          },
        ],
        AlarmInclusions: missingAlarmTypeIds.map((alarmTypeId) => ({
          AlarmType: { AlarmTypeId: alarmTypeId },
        })),
      }).unwrap();

      dispatch(
        showSnackbar({
          message: t('alarm_setup.success', {
            defaultValue: 'EnergyXplorer alarms are now enabled for your user.',
          }),
          severity: 'success',
        }),
      );
    } catch {
      dispatch(
        showSnackbar({
          message: t('alarm_setup.error', {
            defaultValue: 'We could not enable EnergyXplorer alarms right now.',
          }),
          severity: 'error',
        }),
      );
    }
  };

  const deleteAutoCreatedAlarmGroup = async () => {
    if (!autoCreatedAlarmGroup) {
      return false;
    }

    const deleteResult = await deleteAlarmGroup(
      autoCreatedAlarmGroup.AlarmGroupId,
    );

    if ('error' in deleteResult) {
      dispatch(
        showSnackbar({
          message: t('alarm_setup.delete_error', {
            defaultValue:
              'We could not delete the automatic EnergyXplorer alarm group right now.',
          }),
          severity: 'error',
        }),
      );

      return false;
    }

    dispatch(
      showSnackbar({
        message: t('alarm_setup.delete_success', {
          defaultValue: 'The automatic EnergyXplorer alarm group was deleted.',
        }),
        severity: 'success',
      }),
    );

    return true;
  };

  return {
    shouldShowSetupPrompt,
    isLoading: isSetupStateLoading,
    isEnabling,
    enableAlarms,
    autoCreatedAlarmGroup,
    canDeleteAutoCreatedAlarmGroup: autoCreatedAlarmGroup !== null,
    isDeletingAutoCreatedAlarmGroup,
    deleteAutoCreatedAlarmGroup,
  };
}
